import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import MarkdownBody from '../components/MarkdownBody';
import api from '../api';
import VersionHistoryDrawer from '../components/VersionHistoryDrawer';
import useSubmitShortcut from '../hooks/useSubmitShortcut';
import { formatDate as formatDateShared } from '../utils/date';
import { useToast } from '../contexts/ToastContext';

export default function PromptDetailPage() {
  const { promptKey } = useParams();
  const { addToast } = useToast();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const editTextareaRef = useRef(null);

  // Version history
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [versionContent, setVersionContent] = useState(null);
  const [previousVersionContent, setPreviousVersionContent] = useState(null);

  // Default-updated banner state
  const [showNewDefault, setShowNewDefault] = useState(false);
  const [newDefaultContent, setNewDefaultContent] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  const fetchPrompt = useCallback(async () => {
    try {
      const res = await api.get(`/prompts/${promptKey}`);
      setPrompt(res.data.prompt);
      setEditContent(res.data.prompt.content);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load prompt:', err);
      setLoading(false);
    }
  }, [promptKey]);

  useEffect(() => {
    fetchPrompt();
  }, [fetchPrompt]);

  const handleSave = async () => {
    if (!editContent.trim()) return;
    setSaving(true);
    try {
      const res = await api.put(`/prompts/${promptKey}`, { content: editContent });
      setPrompt(res.data.prompt);
      setEditContent(res.data.prompt.content);
      setEditing(false);
    } catch (err) {
      console.error('Failed to save prompt:', err);
      // The backend refuses a prompt whose {user_export} is malformed
      // or uncapped on a non-Pro plan; its message says what to change.
      addToast(err?.response?.data?.error || 'Failed to save prompt.', 10000);
    }
    setSaving(false);
  };

  const handleOpenHistory = async () => {
    setDrawerOpen(true);
    try {
      const res = await api.get(`/prompts/${promptKey}/versions`);
      setVersions(res.data.versions);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  // A version's content — id may be the synthetic 'default' (file default,
  // #128), which lives on its own endpoint.
  const fetchPromptVersionContent = async (id) => {
    const res = await api.get(id === 'default'
      ? `/prompts/${promptKey}/default`
      : `/prompts/${promptKey}/versions/${id}`);
    return res.data.prompt.content;
  };

  const handleSelectVersion = async (id) => {
    setSelectedVersionId(id);
    setVersionContent(null);
    setPreviousVersionContent(null);
    try {
      // versions is newest-first; the next entry is the previous version,
      // fetched alongside so the drawer can render a diff.
      const idx = versions.findIndex(v => v.id === id);
      const prev = idx >= 0 ? versions[idx + 1] : null;
      const [content, prevContent] = await Promise.all([
        fetchPromptVersionContent(id),
        prev ? fetchPromptVersionContent(prev.id) : Promise.resolve(null),
      ]);
      setVersionContent(content);
      if (prevContent != null) setPreviousVersionContent(prevContent);
    } catch (err) {
      console.error('Failed to load version:', err);
    }
  };

  const handleRevert = async (id) => {
    try {
      const endpoint = id === 'default'
        ? `/prompts/${promptKey}/revert-to-default`
        : `/prompts/${promptKey}/revert/${id}`;
      const res = await api.post(endpoint);
      setPrompt(res.data.prompt);
      setEditContent(res.data.prompt.content);
      setDrawerOpen(false);
      setSelectedVersionId(null);
      setVersionContent(null);
    } catch (err) {
      console.error('Failed to revert:', err);
    }
  };

  const handleViewNewDefault = async () => {
    try {
      const res = await api.get(`/prompts/${promptKey}/default`);
      setNewDefaultContent(res.data.prompt.content);
      setShowNewDefault(true);
    } catch (err) {
      console.error('Failed to load new default:', err);
    }
  };

  const handleAcceptNewDefault = async () => {
    try {
      const res = await api.post(`/prompts/${promptKey}/revert-to-default`);
      setPrompt(res.data.prompt);
      setEditContent(res.data.prompt.content);
      setShowNewDefault(false);
      setNewDefaultContent(null);
    } catch (err) {
      console.error('Failed to accept new default:', err);
    }
  };

  const handleDismissDefault = async () => {
    setDismissing(true);
    try {
      const res = await api.post(`/prompts/${promptKey}/acknowledge-default`);
      setPrompt(res.data.prompt);
      setShowNewDefault(false);
      setNewDefaultContent(null);
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
    setDismissing(false);
  };

  // Cmd+Return / Ctrl+Enter saves the prompt while editing (#129).
  useSubmitShortcut(editTextareaRef, () => handleSave(), editing && !saving && !!editContent.trim());

  const formatDate = (iso) => formatDateShared(iso, { fallback: 'default' });

  const generatedByLabel = (g) => {
    if (g === 'default') return 'default';
    if (g === 'user') return 'edited manually';
    if (g === 'revert') return 'reverted';
    return g;
  };

  if (loading) {
    return (
      <div style={{ padding: '60px 24px', maxWidth: '800px', margin: '0 auto' }}>
        <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div style={{ padding: '60px 24px', maxWidth: '800px', margin: '0 auto' }}>
        <p style={{ color: 'var(--text-muted)' }}>Prompt not found.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '60px 24px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Back link */}
      <Link to="/prompts" style={{
        fontFamily: 'var(--sans)',
        fontSize: '0.75rem',
        fontWeight: 300,
        color: 'var(--text-muted)',
        textDecoration: 'none',
        opacity: 0.7,
        display: 'inline-block',
        marginBottom: '16px',
      }}>
        &larr; All prompts
      </Link>

      {/* Header */}
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
        <h1 style={{
          fontFamily: 'var(--serif)',
          fontSize: '2rem',
          fontWeight: 300,
          color: 'var(--text-primary)',
          margin: 0,
        }}>
          {prompt.title}
        </h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => {
              if (editing) { handleSave(); } else { setEditing(true); setEditContent(prompt.content); }
            }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: 'var(--sans)', fontSize: '0.8rem', fontWeight: 300,
              color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <span style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: 'var(--success)', display: 'inline-block',
            }} />
            v{prompt.version_number} &middot; {formatDate(prompt.created_at)}
          </button>
          <button
            onClick={handleOpenHistory}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: 'var(--sans)', fontSize: '0.75rem', fontWeight: 300,
              color: 'var(--text-muted)', opacity: 0.7,
              textDecoration: 'underline',
            }}
          >
            history
          </button>
        </div>
      </div>

      {/* Meta line */}
      <p style={{
        fontFamily: 'var(--sans)',
        fontSize: '0.75rem',
        fontWeight: 300,
        color: 'var(--text-muted)',
        margin: '0 0 16px 0',
        opacity: 0.7,
      }}>
        {generatedByLabel(prompt.generated_by)}
        {prompt.created_at && <> &middot; {formatDate(prompt.created_at)}</>}
      </p>

      {/* Accent divider */}
      <div style={{ height: '1px', background: 'var(--accent-dim)', opacity: 0.3, marginBottom: '24px' }} />

      {/* Default updated banner */}
      {prompt.default_updated && (
        <div style={{
          background: 'var(--accent-subtle, rgba(74, 222, 128, 0.08))',
          border: '1px solid var(--accent-dim, rgba(74, 222, 128, 0.2))',
          borderRadius: '8px',
          padding: '14px 18px',
          marginBottom: '20px',
        }}>
          <p style={{
            fontFamily: 'var(--sans)',
            fontSize: '0.8rem',
            fontWeight: 400,
            color: 'var(--text-secondary)',
            margin: '0 0 10px 0',
          }}>
            The default prompt has been updated.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handleViewNewDefault}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontFamily: 'var(--sans)', fontSize: '0.75rem', fontWeight: 400,
                color: 'var(--accent)', textDecoration: 'underline',
              }}
            >
              View new default
            </button>
            <button
              onClick={handleAcceptNewDefault}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontFamily: 'var(--sans)', fontSize: '0.75rem', fontWeight: 400,
                color: 'var(--accent)', textDecoration: 'underline',
              }}
            >
              Accept new default
            </button>
            <button
              onClick={handleDismissDefault}
              disabled={dismissing}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontFamily: 'var(--sans)', fontSize: '0.75rem', fontWeight: 400,
                color: 'var(--text-muted)', textDecoration: 'underline',
                opacity: dismissing ? 0.5 : 0.7,
              }}
            >
              Dismiss
            </button>
          </div>
          {showNewDefault && newDefaultContent && (
            <pre style={{
              fontFamily: 'var(--sans)',
              fontSize: '0.8rem',
              fontWeight: 300,
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              marginTop: '12px',
              padding: '12px',
              background: 'var(--bg-input, rgba(0,0,0,0.15))',
              borderRadius: '6px',
              maxHeight: '300px',
              overflow: 'auto',
            }}>
              {newDefaultContent}
            </pre>
          )}
        </div>
      )}

      {/* Edit mode */}
      {editing && (
        <div>
          <textarea
            ref={editTextareaRef}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            style={{
              width: '100%',
              minHeight: '500px',
              background: 'var(--bg-input)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--mono, var(--sans))',
              fontSize: '0.8rem',
              fontWeight: 300,
              padding: '16px',
              lineHeight: 1.6,
              resize: 'vertical',
            }}
          />
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 20px',
                background: 'var(--accent)',
                border: 'none',
                borderRadius: '6px',
                color: 'var(--bg-deep)',
                fontFamily: 'var(--sans)',
                fontSize: '0.85rem',
                fontWeight: 400,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => { setEditing(false); setEditContent(prompt.content); }}
              style={{
                padding: '8px 20px',
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text-muted)',
                fontFamily: 'var(--sans)',
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Display mode */}
      {!editing && (
        <MarkdownBody style={{
          fontFamily: 'var(--sans)',
          fontSize: '0.85rem',
          fontWeight: 300,
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
          wordBreak: 'break-word',
        }}>
          {prompt.content}
        </MarkdownBody>
      )}

      {/* Version History Drawer */}
      <VersionHistoryDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedVersionId(null); setVersionContent(null); setPreviousVersionContent(null); }}
        title={`${prompt.title} History`}
        versions={versions}
        selectedVersionId={selectedVersionId}
        onSelectVersion={handleSelectVersion}
        versionContent={versionContent}
        previousVersionContent={previousVersionContent}
        onRevert={handleRevert}
      />
    </div>
  );
}
